import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link2, MessageCircle, X, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import UserAvatar from '@/components/ui/UserAvatar';

interface MatchCelebrationProps {
  isOpen: boolean;
  onClose: (matchId?: string) => void;
  onStartChat: () => void;
  onKeepSwiping: (matchId?: string) => void;
  matchedUser: {
    name: string;
    photo: string;
    id: string;
  };
  currentUser: {
    name: string;
    photo: string;
    id: string;
  };
  matchId?: string | null;
  isStartingChat?: boolean;
}

export const MatchCelebration: React.FC<MatchCelebrationProps> = ({
  isOpen,
  onClose,
  onStartChat,
  onKeepSwiping,
  matchedUser,
  currentUser,
  matchId,
  isStartingChat = false
}) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    console.log('🎉 MatchCelebration: isOpen =', isOpen, 'matchedUser =', matchedUser, 'currentUser =', currentUser);
    if (isOpen) {
      console.log('🎉 MatchCelebration: Opening celebration modal!');
      setShowConfetti(true);
      // Auto-hide confetti after animation
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, matchedUser, currentUser]);

  const confettiParticles = Array.from({ length: 50 }, (_, i) => (
    <motion.div
      key={i}
      className="absolute w-2 h-2 bg-pink-400 rounded-full"
      initial={{
        x: Math.random() * window.innerWidth,
        y: -10,
        rotate: 0,
        scale: Math.random() * 0.5 + 0.5
      }}
      animate={{
        y: window.innerHeight + 10,
        rotate: 360,
        transition: {
          duration: Math.random() * 2 + 2,
          ease: "easeOut"
        }
      }}
      style={{
        backgroundColor: ['#f472b6', '#fb7185', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa'][Math.floor(Math.random() * 6)]
      }}
    />
  ));

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        >
          {/* Confetti Animation */}
          {showConfetti && (
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
              {confettiParticles}
            </div>
          )}

          {/* Main Modal */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative bg-gray-900 border border-gray-700 rounded-3xl p-8 mx-4 max-w-md w-full shadow-2xl"
          >
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onClose(matchId ?? undefined)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Header with Animation */}
            <div className="text-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", damping: 15 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full mb-4"
              >
                <Link2 className="h-8 w-8 text-white" />
              </motion.div>

              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-3xl font-bold bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent mb-2"
              >
                It's a Tie!
              </motion.h1>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4 }}
                className="flex items-center justify-center space-x-2 text-yellow-400"
              >
                <Sparkles className="h-5 w-5" />
                <Sparkles className="h-6 w-6" />
                <Sparkles className="h-5 w-5" />
              </motion.div>
            </div>

            {/* User Photos */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-center space-x-6 mb-8"
            >
              {/* Current User Photo */}
              <div className="relative">
                <div className="w-20 h-20 rounded-full ring-4 ring-pink-500/30 ring-offset-4 ring-offset-gray-900">
                  <UserAvatar
                    user={{
                      id: currentUser.id,
                      name: currentUser.name,
                      photo_urls: currentUser.photo ? [currentUser.photo] : undefined
                    }}
                    size={80}
                    className="w-full h-full"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center shadow-lg">
                  <Link2 className="h-4 w-4 text-white" />
                </div>
              </div>

              {/* Connection Animation */}
              <motion.div
                animate={{
                  scale: [1, 1.3, 1],
                  rotate: [0, 15, -15, 0]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
                className="text-pink-400"
              >
                <Link2 className="h-10 w-10 drop-shadow-lg" />
              </motion.div>

              {/* Matched User Photo */}
              <div className="relative">
                <div className="w-20 h-20 rounded-full ring-4 ring-pink-500/30 ring-offset-4 ring-offset-gray-900">
                  <UserAvatar
                    user={{
                      id: matchedUser.id,
                      name: matchedUser.name,
                      photo_urls: matchedUser.photo ? [matchedUser.photo] : undefined
                    }}
                    size={80}
                    className="w-full h-full"
                  />
                </div>
                <div className="absolute -bottom-1 -left-1 w-8 h-8 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center shadow-lg">
                  <Link2 className="h-4 w-4 text-white" />
                </div>
              </div>
            </motion.div>

            {/* Tie Message */}
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-center text-gray-300 mb-8 text-lg"
            >
              You and <span className="font-semibold text-pink-400">{matchedUser.name}</span> formed a tie.
            </motion.p>

            {/* Action Buttons */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="space-y-3"
            >
              <Button
                onClick={onStartChat}
                disabled={isStartingChat}
                className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white py-3 rounded-xl font-semibold shadow-lg transform transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                {isStartingChat ? 'Starting Chat...' : 'Start Chatting'}
              </Button>

              <Button
                onClick={() => onKeepSwiping(matchId ?? undefined)}
                variant="outline"
                className="w-full border-2 border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white py-3 rounded-xl font-semibold transition-all duration-200"
              >
                Keep Swiping
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MatchCelebration;

